import { QuaggaJSCodeReader } from "@ericblade/quagga2";
import {
  Button,
  DEFAULT_PAGE_WIDTH,
  Page,
  VStack,
  View,
  useFlashMessages,
  useThemeProvider,
} from "@towersystems/roam-common-native";
import { clamp } from "lodash";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { Pressable, useWindowDimensions } from "react-native";
import { Spacings } from "react-native-ui-lib";
import { LoadingScreen, SortableGrid } from "../../../../../../components";
import {
  DragItem,
  ItemTypes,
  SortItem,
} from "../../../../../../components/DragDrop/types";
import { useBarcodeSettingsProvider } from "../../../../../../utilities/barcode-scanner/hooks";
import { ScannerSettingsItem } from "../../../../../../utilities/barcode-scanner/types";
import { ScannerSettingsItemRow } from "./components/ScannerSettingsItemRow";
import { BarcodeReaders } from "../../../../../../utilities/barcode-scanner/constants";
import React from "react";
import { MaterialIcons } from "@expo/vector-icons";
import { useNavigation } from "@react-navigation/native";

export type BarcodeSettingsScreenProps = {};

type FormValues = Record<
  QuaggaJSCodeReader,
  ScannerSettingsItem<QuaggaJSCodeReader>
>;

const LIST_ITEM_HEIGHT = 92;

export const BarcodeSettingsScreen = ({}: BarcodeSettingsScreenProps) => {
  const { bootstrapping, readers, updateReaders, save } =
    useBarcodeSettingsProvider();

  const navigation = useNavigation<any>();

  const [saving, setSaving] = useState<boolean>(false);

  const { showMessage } = useFlashMessages();

  const [gridKey, setGridKey] = useState<number>(1);

  const windowDimensions = useWindowDimensions();

  const methods = useForm<FormValues>({
    mode: "all",
    reValidateMode: "onChange",
    defaultValues: readers,
  });

  const {
    formState: { isDirty },
  } = methods;

  const data = methods.watch();

  function handleDragEnd(newOrder: string[]) {
    const commonOptions = { shouldDirty: true };

    newOrder.forEach((_reader, i) => {
      const reader = _reader as QuaggaJSCodeReader;
      methods.setValue(
        reader,
        { ...readers[reader], position: i },
        commonOptions
      );
    });
  }

  function handleChecked(id: QuaggaJSCodeReader) {
    const current = methods.getValues();
    current[id].enabled = !current[id].enabled;
    methods.setValue(id, current[id], { shouldDirty: true });
  }

  function handleSaveFailed() {
    showMessage({
      message: "Failed to save scanner settings",
      severity: "error",
    });
  }

  async function handleApply(formValues: FormValues) {
    setSaving(true);
    return save(formValues)
      .then(() => {
        updateReaders(formValues);
      })
      .catch(handleSaveFailed)
      .finally(() => {
        setSaving(false);
        navigation.goBack();
      });
  }

  function renderItem(
    item: DragItem<ScannerSettingsItem<QuaggaJSCodeReader>> | SortItem
  ) {
    if (item.type === ItemTypes.Sortable) {
      return <></>;
    }

    return (
      <ScannerSettingsItemRow
        item={item.data}
        label={BarcodeReaders[item.data.value].label}
        handleChecked={() => handleChecked(item.data.value)}
      />
    );
  }

  function refreshGrid() {
    setGridKey((prev) => (prev += 1));
  }

  useEffect(() => {
    methods.reset(readers);
  }, [readers]);

  useEffect(() => {
    refreshGrid();
  }, [windowDimensions]);

  const dataMap = new Map(
    Object.entries(data).map(([key, reader]) => [
      key,
      {
        id: reader.value,
        type: ItemTypes.Draggable,
        data: reader,
      },
    ])
  );

  const usablePageWidth = clamp(windowDimensions.width, DEFAULT_PAGE_WIDTH);

  const commonFooterActionProps = {
    isAction: true,
    fullWidth: true,
    loading: saving,
    disabled: bootstrapping || saving || !isDirty,
  };

  const footerMarkup = (
    <VStack>
      <Button
        {...commonFooterActionProps}
        onPress={() => {
          methods.reset(readers);
          refreshGrid();
        }}
        isDestructive
        label="Cancel"
      />
      <Button
        {...commonFooterActionProps}
        onPress={methods.handleSubmit(handleApply, handleSaveFailed)}
        label="Apply"
      />
    </VStack>
  );

  const pagePadding = Spacings.s5;

  return (
    <Page cover footer={footerMarkup} scrollable={false}>
      {bootstrapping ? (
        <VStack centerHorizontal>
          <LoadingScreen message="Loading scanner settings" />
        </VStack>
      ) : (
        <VStack
          fill
          centerHorizontal
          style={{ marginLeft: -pagePadding, marginRight: -pagePadding }}
        >
          <SortableGrid
            key={gridKey}
            data={dataMap}
            initialOrder={Object.values(data).map((d) => d.value)}
            renderItem={renderItem}
            onOrderingFinished={handleDragEnd}
            DragHandleContainer={DragHandleContainer}
            DragHandleComponent={React.forwardRef((props: any, ref: any) => (
              <DragHandle ref={ref} {...props} />
            ))}
            itemWidth={usablePageWidth}
            itemHeight={LIST_ITEM_HEIGHT}
            numColumns={1}
            itemMargin={1}
            offsetY={0}
          />
        </VStack>
      )}
    </Page>
  );
};

function DragHandleContainer({ children }: React.PropsWithChildren<{}>) {
  return (
    <View
      style={{
        position: "absolute",
        display: "flex",
        justifyContent: "center",
        top: 0,
        right: Spacings.s5 * 2,
        bottom: 0,
      }}
    >
      {children}
    </View>
  );
}

const DragHandle = React.forwardRef<any, {}>(({ ...props }, ref) => {
  const { spacings, colors } = useThemeProvider();

  const commonPressableProps = {
    backgroundColor: "rgba(0, 0, 0, 0.2)",
    borderRadius: spacings.s2,
  };

  const commonIconProps = {
    size: 24,
    color: colors.white,
  };

  return (
    <Pressable ref={ref} {...props} style={commonPressableProps}>
      <MaterialIcons {...commonIconProps} name="drag-handle" />
    </Pressable>
  );
});
